/**
 * Nostr kľúče a signing (S11, F4AI-17).
 *
 * Postup podľa zadania: NIP-04 nepoužívame (nie je čo šifrovať), podpis ide
 * čisto NIP-01 — `finalizeEvent` z `nostr-tools/pure` dopočíta `id`
 * (SHA-256 zo `[0, pubkey, created_at, kind, tags, content]`) a pripojí
 * BIP-340 Schnorr podpis. Žiadna sieť: signing je čistá funkcia, preto ho
 * testujeme bez relaya (`test/keys.test.ts`).
 *
 * Kľúč uložujeme v `localStorage` pod kľúčom `forum4ai_nsec` ako NIP-19
 * `nsec1…` reťazec. Verejnú časť zobrazujeme ako `npub1…` (`nip19.npubEncode`).
 *
 * Importy idú cez subpathy `nostr-tools/pure` a `nostr-tools/nip19`, nie
 * cez hlavný entry point — hlavný balík ťahá relay/WebSocket kód, ktorý
 * v browser bundli nechceme.
 */
import { finalizeEvent, generateSecretKey, getEventHash, getPublicKey } from 'nostr-tools/pure';
import { decode as decodeNip19, npubEncode, nsecEncode } from 'nostr-tools/nip19';

import type { NostrEvent, NostrTag } from '@forum4ai/shared';

/** kľúč v `localStorage`, podľa zadania F4AI-17 */
export const NSEC_STORAGE_KEY = 'forum4ai_nsec';

/** kind 1023 = odpoveď (post) v registry forum4AI (`packages/shared/src/kinds.ts`) */
export const KIND_POST = 1023;

/** minimálne rozhranie, ktoré potrebuje `localStorage` — testy injectnú vlastnú implementáciu */
export interface KeyStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** `localStorage` ak existuje (browser), inak `null` (SSR, node test bez DOM) */
export function browserStorage(): KeyStorage | null {
  try {
    const storage = globalThis.localStorage;
    return storage !== undefined && storage !== null ? (storage as KeyStorage) : null;
  } catch {
    // Safari private mode hádže prístup na localStorage
    return null;
  }
}

/** in-memory úložisko pre testy a prostredia bez `localStorage` */
export function createMemoryStorage(initial: Record<string, string> = {}): KeyStorage {
  const map = new Map<string, string>(Object.entries(initial));
  return {
    getItem: (key) => map.get(key) ?? null,
    setItem: (key, value) => {
      map.set(key, value);
    },
    removeItem: (key) => {
      map.delete(key);
    },
  };
}

export interface Identity {
  /** 32 B tajný kľúč */
  secretKey: Uint8Array;
  /** 64-hex x-only pubkey */
  pubkey: string;
  /** NIP-19 `npub1…` — to, čo UI ukazuje verejne */
  npub: string;
  /** NIP-19 `nsec1…` — formát, ktorý ukladáme do `localStorage` */
  nsec: string;
  /** true, ak sme kľúč práve vygenerovali (nová identita) */
  created: boolean;
}

const HEX_64 = /^[0-9a-f]{64}$/;

/**
 * B6: normalizácia vloženej hodnoty kľúča — používatelia lepia kľúče z
 * iných toolov s medzerami, VEĽKÝMI písmenami alebo `0x` prefixom.
 * Bech32 (`nsec1…`) je v Nostr definovaný ako lowercase, hex skala sa
 * tiež porovnáva lowercase → normalizácia je voľná a nič nenesie.
 */
export function normalizeNsecInput(value: string): string {
  let normalized = value.trim().toLowerCase();
  if (normalized.startsWith('0x')) normalized = normalized.slice(2);
  return normalized.trim();
}

function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(32);
  for (let i = 0; i < 32; i += 1) bytes[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return bytes;
}

/**
 * Rozpozná obe bežné podoby tajného kľúča: NIP-19 `nsec1…` aj surový
 * 64-hex (užívatelia ho lepia z iných toolov).
 */
export function secretKeyFromNsec(value: string): Uint8Array {
  const trimmed = normalizeNsecInput(value);
  if (HEX_64.test(trimmed)) return hexToBytes(trimmed);
  const decoded = decodeNip19(trimmed);
  if (decoded.type !== 'nsec') {
    throw new Error(`čakali sme nsec1… alebo 64-hex kľúč, dostali sme ${decoded.type}`);
  }
  return decoded.data;
}

/** identita zo zadaného `nsec` (testy deterministicky overia id/pubkey) */
export function identityFromNsec(nsec: string): Identity {
  const secretKey = secretKeyFromNsec(nsec);
  const pubkey = getPublicKey(secretKey);
  return { secretKey, pubkey, npub: npubEncode(pubkey), nsec: nsecEncode(secretKey), created: false };
}

/**
 * Prečíta identitu z úložiska; keď tam nič nie je, vygeneruje nový kľúč
 * a uloží ho. Chýbajúce úložisko (SSR) → dočasná identita bez uloženia.
 */
export function loadOrCreateIdentity(storage?: KeyStorage | null): Identity {
  const store = storage === undefined ? browserStorage() : storage;
  const stored = store?.getItem(NSEC_STORAGE_KEY) ?? null;
  if (stored !== null && stored.trim() !== '') {
    try {
      return identityFromNsec(stored);
    } catch {
      // poškodená hodnota — regenerujeme a prepíšeme ju, namiesto
      // zlyhania celej aplikácie
    }
  }
  const secretKey = generateSecretKey();
  const nsec = nsecEncode(secretKey);
  const pubkey = getPublicKey(secretKey);
  try {
    store?.setItem(NSEC_STORAGE_KEY, nsec);
  } catch {
    // plné/zamknuté úložisko: identita platí pre túto session
  }
  return { secretKey, pubkey, npub: npubEncode(pubkey), nsec, created: true };
}

/** zahoď identitu z úložiska (UI tlačidlo „reset identity“) */
export function clearIdentity(storage?: KeyStorage | null): void {
  const store = storage === undefined ? browserStorage() : storage;
  try {
    store?.removeItem(NSEC_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export interface SignParams {
  kind: number;
  content: string;
  tags?: NostrTag[] | string[][];
  secretKey: Uint8Array;
  /** sekundy; default `Math.floor(Date.now()/1000)` — testy dodajú fix, aby
      bol `id` deterministický */
  createdAt?: number;
}

/**
 * Podpíše event NIP-01 (`finalizeEvent`) a vráti ho ako shared
 * `NostrEvent` (7 polí). `getEventHash` kontrolu pridávame len ako
 * poistku — nostr-tools už `id` počíta z rovnakej serializácie.
 */
export function signEvent(params: SignParams): NostrEvent {
  const template = {
    kind: params.kind,
    content: params.content,
    tags: (params.tags ?? []) as string[][],
    created_at: params.createdAt ?? Math.floor(Date.now() / 1000),
  };
  const signed = finalizeEvent(template, params.secretKey);
  const event: NostrEvent = {
    id: signed.id,
    pubkey: signed.pubkey,
    created_at: signed.created_at,
    kind: signed.kind,
    tags: signed.tags as NostrTag[],
    content: signed.content,
    sig: signed.sig,
  };
  if (getEventHash({ ...signed }) !== event.id) {
    throw new Error('interná chyba: id eventu nezodpovedá hashu obsahu');
  }
  return event;
}

/**
 * Tagy odpovede podľa našej L2 politiky (`packages/shared/src/validators.ts`
 * `validatePostRootMarker`): práve JEDEN `e` tag s markerom `root`.
 * Priama odpoveď na hlavu vlákna → `root` aj `parent` smerujú na root id,
 * ale root marker ostáva práve jeden (parent marker je samostatný tag).
 */
export function buildReplyTags(
  rootId: string,
  options: { parentId?: string | null; topicAddress?: string | null; topicSlug?: string | null } = {},
): NostrTag[] {
  const tags: NostrTag[] = [];
  const parentId = options.parentId ?? rootId;
  tags.push(['e', rootId, 'root']);
  if (parentId !== rootId) tags.push(['e', parentId, 'parent']);
  if (options.topicAddress) tags.push(['a', options.topicAddress]);
  if (options.topicSlug) tags.push(['t', options.topicSlug]);
  return tags;
}

/** nadpis + text na jedno textové pole (NIP-01 `content` je plain text) */
export function composeContent(title: string, text: string): string {
  const heading = title.trim();
  const body = text.trim();
  if (heading === '') return body;
  return body === '' ? `# ${heading}` : `# ${heading}\n\n${body}`;
}

export interface ReplyParams {
  rootId: string;
  title?: string;
  text: string;
  secretKey: Uint8Array;
  parentId?: string | null;
  topicAddress?: string | null;
  topicSlug?: string | null;
  createdAt?: number;
}

/** odpoveď kind 1023 pripravená na `POST /events` */
export function signReply(params: ReplyParams): NostrEvent {
  return signEvent({
    kind: KIND_POST,
    content: composeContent(params.title ?? '', params.text),
    tags: buildReplyTags(params.rootId, {
      parentId: params.parentId,
      topicAddress: params.topicAddress,
      topicSlug: params.topicSlug,
    }),
    secretKey: params.secretKey,
    ...(params.createdAt !== undefined ? { createdAt: params.createdAt } : {}),
  });
}
